import {
  ActionMenu,
  ConfirmationDialog,
  DataGrid,
  FilterToolbar,
  useNotifications
} from '@ijoin/components';
import {
  GetAdminPlanAdvisorsRes,
  getGetAdminPlanAdvisorsQueryKey,
  useDeleteAdminPlanAdvisor,
  useGetAdminAdvisoryOrgAdvisors,
  useGetAdminPlanAdvisors
} from '@ijoin/hooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { camelCase } from 'lodash';
import { FC, useState } from 'react';
import { useToggle } from 'usehooks-ts';

import { ApiError } from '../types';
import { AddPlanAdvisorDialog } from './AddPlanAdvisorDialog';

const columns: GridColDef<GetAdminPlanAdvisorsRes>[] = [
  {
    field: 'firstName',
    flex: 1,
    headerName: 'First Name'
  },
  {
    field: 'lastName',
    flex: 1,
    headerName: 'Last Name'
  },
  {
    field: 'email',
    flex: 1.5,
    headerName: 'Email'
  },
  {
    field: 'isPrimaryAdvisor',
    flex: 0.75,
    headerName: 'Primary',
    renderCell: params => (params.value ? 'Yes' : 'No')
  },
  {
    field: 'isAdmin',
    flex: 0.5,
    headerName: 'Admin',
    renderCell: params => (params.value ? 'Yes' : 'No')
  },
  {
    field: 'hasAllPlans',
    flex: 0.5,
    headerName: 'All Plans',
    renderCell: params => (params.value ? 'Yes' : 'No')
  },
  {
    field: 'advisorId',
    flex: 1,
    headerName: 'Advisor ID',
    sortable: false
  }
];

export const PlanAdvisorsTab: FC<{
  advisoryOrganizationId: string;
  planId: string;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const [isAddOpen, toggleAddOpen] = useToggle();
  const [removeTarget, setRemoveTarget] =
    useState<GetAdminPlanAdvisorsRes | null>(null);
  const advisors = useGetAdminPlanAdvisors(props.planId);
  const orgAdvisors = useGetAdminAdvisoryOrgAdvisors(
    props.advisoryOrganizationId,
    { hasAllPlans: false, take: 1000 }
  );

  const removeAdvisor = useDeleteAdminPlanAdvisor<ApiError>({
    mutation: {
      onError: error =>
        notifications.error(
          error.message || 'Failed to remove advisor from plan'
        ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetAdminPlanAdvisorsQueryKey(props.planId)
        });
        notifications.success('Advisor removed from plan');
      }
    }
  });

  const onCancelRemove = () => setRemoveTarget(null);

  const onConfirmRemove = () => {
    if (!removeTarget) return;
    removeAdvisor.mutate({
      advisorId: removeTarget.advisorId,
      planId: props.planId
    });
    setRemoveTarget(null);
  };

  const advisorOptions = (orgAdvisors.data?.data ?? [])
    .filter(
      advisor =>
        !(advisors.data ?? []).some(
          linked => linked.advisorId === advisor.advisorId
        )
    )
    .map(advisor => ({
      label: `${advisor.firstName} ${advisor.lastName}`,
      value: advisor.advisorId
    }));

  const columnsWithActions: GridColDef<GetAdminPlanAdvisorsRes>[] = [
    ...columns,
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <ActionMenu
          actions={[
            {
              content: 'Impersonate',
              onSelect: () => {
                window.open(
                  `/api/v3/admin/advisory-organizations/${props.advisoryOrganizationId}/advisors/${params.row.advisorId}/impersonate`,
                  '_blank'
                );
              }
            },
            {
              content: 'Remove From Plan',
              destructive: true,
              disabled: params.row.hasAllPlans,
              onSelect: () => setRemoveTarget(params.row),
              tooltip: params.row.hasAllPlans
                ? 'Advisor has all plans'
                : undefined
            }
          ]}
          trigger={
            <IconButton
              data-testid={camelCase(
                `actions ${params.row.firstName} ${params.row.lastName}`
              )}>
              <MoreVertIcon />
            </IconButton>
          }
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      <DataGrid
        autoHeight={false}
        columns={columnsWithActions}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.advisorId}
        loading={advisors.isFetching}
        rows={advisors.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'planAdvisors'
          },
          toolbar: {
            addLabel: 'Advisor',
            addTestId: 'addPlanAdvisor',
            onAdd: toggleAddOpen
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sx={{ flex: 1, minHeight: 0 }}
      />
      {isAddOpen && (
        <AddPlanAdvisorDialog
          advisorOptions={advisorOptions}
          onClose={toggleAddOpen}
          planId={props.planId}
        />
      )}
      <ConfirmationDialog
        confirmColor='error'
        confirmText='Remove'
        data-testid='removePlanAdvisor'
        isOpen={!!removeTarget}
        message={`Remove ${removeTarget?.firstName ?? ''} ${removeTarget?.lastName ?? ''} from this plan?`}
        onCancel={onCancelRemove}
        onConfirm={onConfirmRemove}
        title='Remove From Plan'
      />
    </Stack>
  );
};

PlanAdvisorsTab.displayName = 'PlanAdvisorsTab';
